import {
  MaterialReactTable,
  MRT_ActionMenuItem,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { FC } from 'react';
import { Link } from 'react-router-dom';
import AddIcon from '@/assets/add.svg';
import DisableIcon from '@/assets/disable.svg';
import EditIcon from '@/assets/edit.svg';
import ViewIcon from '@/assets/view.svg';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerProps } from '@/components/ui/drawer';
import { HeadingGroup } from '@/components/ui/heading-group';
import { Modal } from '@/components/ui/modal';
import { strings } from '@/constants/strings';
import { urls } from '@/constants/urls';
import { useCloseInvalidDrawer } from '@/hooks/use-close-invalid-drawer';
import { useOpenState } from '@/hooks/use-open-state';
import { useOrganisations } from '@/hooks/use-organisations';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';
import { api } from '@/redux/api';
import { composeTableOptions } from '@/table';
import { Organisation } from '@/types/organisation';
import { findById } from '@/utils/find-by-id';
import { CreateOrganisationForm } from './drawer-views/create-organisation-form';
import { EditOrganisationForm } from './drawer-views/edit-organisation-form';
import { ViewOrganisation } from './drawer-views/view-organisation';

const columns: MRT_ColumnDef<Organisation>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    grow: true,
    size: 150,
    Cell: ({ renderedCellValue, row }) => (
      <Link to={urls.organisations.organisation.base.compose(row.original.id)}>
        {renderedCellValue}
      </Link>
    ),
  },
];

type OpenContext = { orgId: number };

type DrawerView = 'view' | 'edit' | 'create';

type DrawerContext = { view: DrawerView } & Partial<OpenContext>;

export const OrganisationsPage: FC = () => {
  const { data: organisations, isLoading: isLoadingOrganisations } =
    useOrganisations();
  const {
    state: drawerState,
    open: openDrawer,
    toggleOpen: toggleDrawerOpen,
  } = useOpenState<DrawerContext>({ view: 'create' });
  const {
    state: deleteConfirmationState,
    open: openDeleteConfirmation,
    toggleOpen: toggleDeleteConfirmationOpen,
  } = useOpenState<OpenContext>({ orgId: -1 });
  const [createOrganisation] = api.useCreateOrganisationMutation();
  const [updateOrganisation] = api.useUpdateOrganisationMutation();
  const [deleteOrganisation] = api.useDeleteOrganisationMutation();

  const table = useMaterialReactTable({
    ...composeTableOptions<Organisation>({ enableRowActions: true }),
    columns,
    enableColumnResizing: true,
    data: organisations,
    state: {
      isLoading: isLoadingOrganisations,
      ...(isLoadingOrganisations
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    renderRowActionMenuItems: ({ row, table, closeMenu }) => [
      <MRT_ActionMenuItem
        icon={<ViewIcon />}
        key="view"
        label="View"
        onClick={() => {
          closeMenu();
          openDrawer({ view: 'view', orgId: row.original.id });
        }}
        table={table}
      />,
      <MRT_ActionMenuItem
        icon={<EditIcon />}
        key="edit"
        label="Edit"
        onClick={() => {
          closeMenu();
          openDrawer({ view: 'edit', orgId: row.original.id });
        }}
        table={table}
      />,
      <MRT_ActionMenuItem
        icon={<DisableIcon />}
        key="disable"
        label="Disable"
        onClick={() => {
          closeMenu();
          openDeleteConfirmation({ orgId: row.original.id });
        }}
        table={table}
      />,
    ],
  });

  const getDrawerTitleProps = (): Pick<DrawerProps, 'title' | 'subtitle'> => {
    switch (drawerState.context.view) {
      case 'create':
        return {
          title: strings.organisations.drawer_create_form.title,
          subtitle: strings.organisations.drawer_create_form.subtitle,
        };
      case 'edit':
        return {
          title: strings.organisations.drawer_edit_form.title,
        };
      case 'view':
        return {
          title: strings.organisations.drawer_view.title,
        };
      default:
        return {
          title: '',
        };
    }
  };

  const drawerOrganisation = findById(
    organisations,
    drawerState.context.orgId ?? -1,
  );
  useCloseInvalidDrawer(
    drawerState.open,
    drawerState.context.view === 'create' || Boolean(drawerOrganisation),
    toggleDrawerOpen,
  );

  const renderDrawerContent = () => {
    switch (drawerState.context.view) {
      case 'create':
        return (
          <CreateOrganisationForm
            onCancel={() => toggleDrawerOpen(false)}
            onSubmit={(data) => {
              createOrganisation(data);
              toggleDrawerOpen(false);
            }}
          />
        );
      case 'edit': {
        const organisation = drawerOrganisation;
        if (organisation) {
          return (
            <EditOrganisationForm
              onCancel={() => toggleDrawerOpen(false)}
              onDisable={() =>
                openDeleteConfirmation({ orgId: organisation.id })
              }
              onSubmit={(data) => {
                updateOrganisation({ ...data, id: organisation.id });
                toggleDrawerOpen(false);
              }}
              organisation={organisation}
            />
          );
        }
        break;
      }
      case 'view': {
        const organisation = drawerOrganisation;
        if (organisation) {
          return (
            <ViewOrganisation
              onDisable={() =>
                openDeleteConfirmation({ orgId: organisation.id })
              }
              onEdit={() => {
                openDrawer({ view: 'edit', orgId: organisation.id });
              }}
              organisation={organisation}
            />
          );
        }
        break;
      }
      default:
        break;
    }

    return null;
  };

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitlePrefix={strings.organisations.subtitle}
          title={strings.organisations.title}
        />
        <Button onClick={() => openDrawer({ view: 'create' })}>
          <AddIcon /> Add Organisation
        </Button>
      </HeadingGroupContainerLayout>
      <MaterialReactTable table={table} />
      <Drawer
        {...getDrawerTitleProps()}
        open={drawerState.open}
        setOpen={toggleDrawerOpen}
      >
        {renderDrawerContent()}
      </Drawer>
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleDeleteConfirmationOpen(false)}
            variant="secondary"
          >
            {strings.organisations.modal_confirm_delete.cancel}
          </Button>,
          <Button
            key="disable"
            onClick={() => {
              deleteOrganisation({ id: deleteConfirmationState.context.orgId });
              toggleDeleteConfirmationOpen(false);
            }}
            variant="primary-warning"
          >
            {strings.organisations.modal_confirm_delete.disable}
          </Button>,
        ]}
        description={strings.organisations.modal_confirm_delete.description}
        open={deleteConfirmationState.open}
        setOpen={toggleDeleteConfirmationOpen}
        title={strings.organisations.modal_confirm_delete.title}
      />
    </>
  );
};
